import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/env';
import HomeKpiSyncLoader from '../components/HomeKpiSyncLoader';

const CRM = {
  orange: '#2365AB',
  orangeDark: '#1C4F86',
  orangeLight: '#FFF1EE',
  navy: '#253342',
  navyMid: '#33475B',
  slate: '#516F90',
  blueLight: '#F5F8FA',
  border: '#DFE3EB',
  text: '#33475B',
  textLight: '#516F90',
  textXlight: '#7C98B6',
  white: '#FFFFFF'
};

function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatHomeDoctorName(nombre) {
  const raw = String(nombre || '').trim();
  if (!raw) return '';
  if (/^(dr|dra)\.?\s/i.test(raw)) return raw;
  return `Dr/a. ${raw}`;
}

function resolveHomeDisplayName(user, profile = null) {
  const adminName = String(
    profile?.administrativo_nombre || user?.administrativo_nombre || ''
  ).trim();
  const medicoName = String(
    profile?.medico_nombre || user?.medico_nombre || ''
  ).trim();

  if (adminName) return adminName;
  if (medicoName) return formatHomeDoctorName(medicoName);

  const rawName = user?.name || user?.username;
  return rawName ? capitalize(String(rawName)) : 'Grupo Bienestar';
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('es-AR');
}

function getPercent(part, total) {
  if (!total) return 0;
  return Math.max(0, Math.min(100, (part / total) * 100));
}

function formatPercent(value) {
  if (!Number.isFinite(value) || value <= 0) return '0%';
  if (value < 1) return `${value.toFixed(1)}%`;
  if (value < 10) return `${value.toFixed(1)}%`;
  return `${Math.round(value)}%`;
}

function formatPrecisePercent(value) {
  if (!Number.isFinite(value)) return '0.00%';
  return `${Number(value).toFixed(2)}%`;
}

function formatShortDate(value) {
  if (!value) return 'sin fecha';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit'
  });
}

function formatSignedPoints(value) {
  if (!Number.isFinite(value) || value === 0) return '0.00 pp';
  const sign = value > 0 ? '+' : '';
  return `${sign}${Number(value).toFixed(2)} pp`;
}

function clampPercent(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Number(value)));
}

function getTodayDateValue() {
  return new Date().toISOString().slice(0, 10);
}

function getMonthStartDateValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

function getDaysAgoDateValue(daysAgo) {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().slice(0, 10);
}

function formatLongDate(value) {
  if (!value) return 'sin fecha';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

function diffDaysInclusive(from, to) {
  if (!from || !to) return 0;
  const start = new Date(`${from}T00:00:00.000Z`);
  const end = new Date(`${to}T00:00:00.000Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.max(0, Math.floor((end - start) / 86400000) + 1);
}

function getTone(percent) {
  if (percent >= 80) return 'good';
  if (percent >= 55) return 'warn';
  return 'bad';
}

const MONTH_LABELS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MONTH_LABELS_FULL_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const HOME_ASSISTANT_AVATAR = '/anita.png';

function formatMonthYearLabel(month, year) {
  const monthIndex = Number(month) - 1;
  const monthLabel = MONTH_LABELS_ES[monthIndex] || String(month);
  const shortYear = String(year || '').slice(-2);
  return shortYear ? `${monthLabel} ${shortYear}` : monthLabel;
}

function formatMonthYearFullLabel(month, year) {
  const monthIndex = Number(month) - 1;
  const monthLabel = MONTH_LABELS_FULL_ES[monthIndex] || String(month);
  return year ? `${monthLabel} ${year}` : monthLabel;
}

function buildSmoothPath(points) {
  if (!points.length) return '';
  let path = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i += 1) {
    const next = points[i];
    path += ` L ${next.x} ${next.y}`;
  }
  return path;
}

function buildAreaPath(points, bottomY) {
  if (!points.length) return '';
  const line = buildSmoothPath(points);
  const last = points[points.length - 1];
  const first = points[0];
  return `${line} L ${last.x} ${bottomY} L ${first.x} ${bottomY} Z`;
}

function TrendLineChart({ labels, fullLabels = [], series, title, loading = false, width = 760, height = 260 }) {
  const [hoveredMonthIndex, setHoveredMonthIndex] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1366
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setViewportWidth(window.innerWidth || 1366);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (loading) {
    return (
      <div style={styles.trendChartCard}>
        <div style={styles.trendChartLoadingWrap}>
          <div style={styles.trendChartSpinner} aria-hidden="true" />
          <div style={styles.trendChartLoadingText}>Procesando últimos meses completos...</div>
        </div>
      </div>
    );
  }

  if (!labels.length || !series.length) {
    return <div style={{ padding: 24, color: '#64748B' }}>No hay datos mensuales disponibles.</div>;
  }

  const chartWidth = width;
  const chartHeight = viewportWidth <= 480 ? 520 : (viewportWidth <= 768 ? 460 : (viewportWidth <= 1024 ? 340 : height));
  const margin = 42;
  const innerWidth = chartWidth - margin * 2;
  const innerHeight = chartHeight - margin * 2;
  const yPadding = 14;
  const yMin = margin + yPadding;
  const yMax = margin + innerHeight - yPadding;
  const yRange = Math.max(1, yMax - yMin);
  const maxValue = Math.max(
    1,
    ...series.flatMap((serie) => serie.data)
  );
  const roundStep = maxValue > 100 ? 50 : maxValue > 20 ? 10 : 5;
  const roundedMaxValue = Math.ceil(maxValue / roundStep) * roundStep;
  const stepX = labels.length > 1 ? innerWidth / (labels.length - 1) : 0;
  const valueToY = (value) => yMax - (value / roundedMaxValue) * yRange;

  const pointsBySeries = series.map((serie) => ({
    ...serie,
    points: serie.data.map((value, index) => ({
      x: margin + stepX * index,
      y: valueToY(value),
      value
    }))
  }));
  const seriesVisualMap = {
    identificador: { dash: '0', areaOpacity: 0.22 },
    cruce: { dash: '8 5', areaOpacity: 0.14 },
    sinCruce: { dash: '3 4', areaOpacity: 0.14 }
  };
  const yTicks = [0, 25, 50, 75, 100].map((percent) => ({
    percent,
    y: yMin + (yRange * percent) / 100,
    value: Math.round(roundedMaxValue * (1 - percent / 100))
  }));

  const hasHover = hoveredMonthIndex != null && hoveredMonthIndex >= 0 && hoveredMonthIndex < labels.length;
  const hoveredX = hasHover ? margin + stepX * hoveredMonthIndex : 0;
  const hoveredLabel = hasHover ? (fullLabels[hoveredMonthIndex] || labels[hoveredMonthIndex]) : '';
  const hoveredValues = hasHover
    ? series.map((serie) => ({
      key: serie.key,
      label: serie.label,
      color: serie.color,
      value: serie.data[hoveredMonthIndex] || 0,
      point: pointsBySeries.find((item) => item.key === serie.key)?.points?.[hoveredMonthIndex]
    }))
    : [];

  const tooltipWidth = 246;
  const tooltipHeight = 88;
  const topPointY = hasHover
    ? Math.min(...hoveredValues.map((item) => item.point?.y ?? yMax))
    : 0;
  const tooltipX = hasHover
    ? Math.max(8, Math.min(chartWidth - tooltipWidth - 8, hoveredX + 12))
    : 0;
  const tooltipY = hasHover
    ? Math.max(8, Math.min(chartHeight - tooltipHeight - 8, topPointY - 52))
    : 0;

  const getNearestMonthIndex = (evt) => {
    if (labels.length <= 1) return 0;

    const svgRect = evt.currentTarget.getBoundingClientRect();
    const scaleX = chartWidth / svgRect.width;
    const x = (evt.clientX - svgRect.left) * scaleX;
    const rawIndex = Math.round((x - margin) / stepX);
    return Math.max(0, Math.min(labels.length - 1, rawIndex));
  };

  return (
    <div style={styles.trendChartCard}>
      <div style={styles.trendChartHeader}>
        <div>
          <div style={styles.trendChartEyebrow}>Evolución mensual</div>
          <div style={styles.trendChartTitle}>{title}</div>
          <div style={styles.trendChartHint}>Comparativo mes a mes de identificadas, cruces y validaciones pendientes.</div>
        </div>
        <div style={styles.trendChartLegend}>
          {series.map((serie) => (
            <div key={serie.key} style={styles.trendChartLegendItem}>
              <span style={{ ...styles.trendChartLegendDot, background: serie.color }} />
              <span>{serie.label}</span>
            </div>
          ))}
        </div>
      </div>

      <svg
        width="100%"
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Gráfico de evolución mensual de recetas"
        style={{ display: 'block', width: '100%', height: 'auto' }}
        onMouseMove={(evt) => setHoveredMonthIndex(getNearestMonthIndex(evt))}
        onMouseLeave={() => setHoveredMonthIndex(null)}
      >
        <defs>
          {series.map((serie) => (
            <linearGradient key={`gradient-${serie.key}`} id={`gradient-${serie.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={serie.color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={serie.color} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>

        <g>
          {yTicks.map((tick) => {
            return (
              <g key={`grid-${tick.percent}`}>
                <line x1={margin} y1={tick.y} x2={chartWidth - margin} y2={tick.y} stroke="#E2E8F0" strokeWidth="1" />
                <text
                  x={margin - 10}
                  y={tick.y + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill="#64748B"
                >
                  {formatNumber(tick.value)}
                </text>
              </g>
            );
          })}

          <line x1={margin} y1={yMin} x2={margin} y2={yMax} stroke="#CBD5E1" strokeWidth="1" />

          {labels.map((label, index) => {
            const x = margin + stepX * index;
            return (
              <text
                key={`label-${label}`}
                x={x}
                y={chartHeight - margin + 18}
                textAnchor="middle"
                fontSize="11"
                fill="#64748B"
              >
                {label}
              </text>
            );
          })}

          {pointsBySeries.map((serie) => (
            <g key={`series-${serie.key}`}>
              <path
                d={buildAreaPath(serie.points, yMax)}
                fill={`url(#gradient-${serie.key})`}
                opacity={seriesVisualMap[serie.key]?.areaOpacity ?? 0.2}
              />
              <path
                d={buildSmoothPath(serie.points)}
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="7"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.95"
              />
              <path
                d={buildSmoothPath(serie.points)}
                fill="none"
                stroke={serie.color}
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={seriesVisualMap[serie.key]?.dash}
              />
              {serie.points.map((point, idx) => (
                <circle
                  key={`${serie.key}-point-${idx}`}
                  cx={point.x}
                  cy={point.y}
                  r={serie.key === 'identificador' ? '4.5' : '4'}
                  fill={serie.color}
                  stroke="#fff"
                  strokeWidth="2"
                />
              ))}
            </g>
          ))}

          {hasHover && (
            <g pointerEvents="none">
              <line
                x1={hoveredX}
                y1={yMin}
                x2={hoveredX}
                y2={yMax}
                stroke="#475569"
                strokeOpacity="0.4"
                strokeDasharray="4 4"
              />
              {hoveredValues.map((item) => (
                <circle
                  key={`hover-highlight-${item.key}`}
                  cx={item.point?.x}
                  cy={item.point?.y}
                  r="6"
                  fill="#fff"
                  stroke={item.color}
                  strokeWidth="3"
                />
              ))}
              <rect
                x={tooltipX}
                y={tooltipY}
                width={tooltipWidth}
                height={tooltipHeight}
                rx="10"
                fill="#0F172A"
                fillOpacity="0.92"
              />
              <text x={tooltipX + 12} y={tooltipY + 18} fontSize="11" fill="#E2E8F0">
                {hoveredLabel}
              </text>
              {hoveredValues.map((item, idx) => (
                <text
                  key={`hover-text-${item.key}`}
                  x={tooltipX + 12}
                  y={tooltipY + 38 + idx * 16}
                  fontSize="11"
                  fill={item.color}
                  fontWeight="700"
                >
                  {item.label}: {formatNumber(item.value)}
                </text>
              ))}
            </g>
          )}
        </g>
      </svg>
    </div>
  );
}

function getScoreMeta(score) {
  if (score >= 80) {
    return {
      label: 'Excelente sincronización',
      description: 'La asociación se encuentra fuerte, consistente y con buena trazabilidad entre ambos sistemas.'
    };
  }
  if (score >= 60) {
    return {
      label: 'Salud operativa estable',
      description: 'La base está bien consolidada, aunque todavía hay margen para mejorar cobertura y cierres de cruce.'
    };
  }
  return {
    label: 'Zona de mejora prioritaria',
    description: 'Hay volumen y datos utiles, pero conviene atacar pendientes de contacto y trazabilidad para elevar el nivel.'
  };
}

function StatPill({ label, value, tone = 'neutral' }) {
  return (
    <div className={`home-stat-pill home-stat-pill--${tone}`}>
      <span className="home-stat-pill__label">{label}</span>
      <strong className="home-stat-pill__value">{value}</strong>
    </div>
  );
}

function InsightMetric({ eyebrow, title, value, hint, tone = 'navy' }) {
  return (
    <div className={`home-insight-card home-insight-card--${tone}`}>
      <span className="home-insight-card__eyebrow">{eyebrow}</span>
      <strong className="home-insight-card__value">{value}</strong>
      <div className="home-insight-card__title">{title}</div>
      <div className="home-insight-card__hint">{hint}</div>
    </div>
  );
}

function ProgressInsight({ title, helper, value, percent, tone = 'good' }) {
  return (
    <div style={styles.progressCard}>
      <div style={styles.progressHead}>
        <div>
          <div style={styles.progressTitle}>{title}</div>
          <div style={styles.progressHelper}>{helper}</div>
        </div>
        <div style={styles.progressValueWrap}>
          <strong style={styles.progressValue}>{value}</strong>
          <span style={styles.progressPercent}>{formatPercent(percent)}</span>
        </div>
      </div>
      <div style={styles.progressTrack}>
        <div
          style={{
            ...styles.progressBar,
            ...progressToneStyles[tone],
            width: `${percent}%`
          }}
        />
      </div>
    </div>
  );
}

function DailyRangeDetailSkeleton({ rowCount = 6 }) {
  const safeCount = Math.min(10, Math.max(4, Number(rowCount) || 6));
  return (
    <div
      className="home-daily-detail-loading"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Cargando detalle diario del rango"
    >
      <div className="home-daily-detail-loading__banner">
        <span className="home-daily-detail-loading__spinner" aria-hidden="true" />
        <div className="home-daily-detail-loading__copy">
          <strong>Cargando detalle por día</strong>
          <span>Armando el desglose del período que elegiste. Esto puede tardar unos segundos.</span>
        </div>
      </div>
      <div className="home-daily-detail-loading__list">
        {Array.from({ length: safeCount }).map((_, index) => (
          <div key={`daily-skel-${index}`} className="home-daily-detail-loading__row" aria-hidden="true">
            <div className="home-daily-detail-loading__date">
              <span className="home-daily-detail-skel home-daily-detail-skel--title" />
              <span className="home-daily-detail-skel home-daily-detail-skel--sub" />
            </div>
            <div className="home-daily-detail-loading__main">
              <span className="home-daily-detail-skel home-daily-detail-skel--line" />
              <span className="home-daily-detail-skel home-daily-detail-skel--bar" />
            </div>
            <div className="home-daily-detail-loading__side">
              <span className="home-daily-detail-skel home-daily-detail-skel--pct" />
              <span className="home-daily-detail-skel home-daily-detail-skel--sub" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const WEEKDAY_PATTERN_METRICS = [
  'Registros del rango',
  'Matches del rango',
  'Solo PLEX',
  'Días sin match'
];

function WeekdayPatternSkeleton() {
  return (
    <div
      className="home-weekday-pattern-loading"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Cargando patrón por día de semana"
    >
      <div className="home-daily-detail-loading__banner">
        <span className="home-daily-detail-loading__spinner" aria-hidden="true" />
        <div className="home-daily-detail-loading__copy">
          <strong>Cargando patrón semanal</strong>
          <span>Calculando totales del rango y el comportamiento por día de la semana.</span>
        </div>
      </div>
      <div className="home-weekday-pattern-loading__metrics home-kpi-range-summary-row">
        {WEEKDAY_PATTERN_METRICS.map((label) => (
          <div key={label} className="home-weekday-pattern-loading__metric" aria-hidden="true">
            <span className="home-daily-detail-skel home-daily-detail-skel--metric-label" />
            <span className="home-daily-detail-skel home-daily-detail-skel--metric-value" />
          </div>
        ))}
      </div>
      <div className="home-weekday-pattern-loading__list">
        {Array.from({ length: 7 }).map((_, index) => (
          <div key={`weekday-skel-${index}`} className="home-weekday-pattern-loading__row" aria-hidden="true">
            <span className="home-daily-detail-skel home-daily-detail-skel--weekday" />
            <span className="home-daily-detail-skel home-daily-detail-skel--line" />
            <span className="home-daily-detail-skel home-daily-detail-skel--pct" />
          </div>
        ))}
      </div>
    </div>
  );
}

function QuickLinkCard({ to, icon, title, description, accentLabel }) {
  return (
    <Link to={to} style={styles.quickLinkCard} className="home-quick-link">
      <div style={styles.quickLinkTop}>
        <span style={styles.quickLinkIcon}>{icon}</span>
        <span style={styles.quickLinkAccent}>{accentLabel}</span>
      </div>
      <div style={styles.quickLinkTitle}>{title}</div>
      <div style={styles.quickLinkDescription}>{description}</div>
      <div style={styles.quickLinkAction}>Abrir vista</div>
    </Link>
  );
}

function RoleKpiCard({ eyebrow, value, hint, loading, accent = '#2365AB' }) {
  return (
    <div
      style={{
        borderRadius: 16,
        background: '#FFFFFF',
        border: '1px solid #E2E8F0',
        padding: '1rem 1.05rem',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 4,
          height: '100%',
          background: accent,
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16
        }}
      />
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: CRM.textXlight
        }}
      >
        {eyebrow}
      </div>
      <div
        style={{
          marginTop: 8,
          fontSize: 32,
          fontWeight: 800,
          lineHeight: 1,
          color: CRM.navy,
          fontVariantNumeric: 'tabular-nums',
          minHeight: 32
        }}
      >
        {loading ? <span style={{ display: 'inline-block', width: 36, height: 18, borderRadius: 6, background: '#E2E8F0' }} /> : formatNumber(value)}
      </div>
      {hint ? (
        <div style={{ marginTop: 10, fontSize: 12, color: CRM.textLight, lineHeight: 1.4 }}>{hint}</div>
      ) : null}
    </div>
  );
}

function useCallCenterKpis({ enabled = true } = {}) {
  const [state, setState] = useState({
    loading: true,
    sinGestionar: 0,
    enFarmacia: 0,
    enPreparacion: 0,
    entregadoCadete: 0,
    finalizados: 0
  });

  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, sinGestionar: 0, enFarmacia: 0, enPreparacion: 0, entregadoCadete: 0, finalizados: 0 });
      return undefined;
    }

    let mounted = true;
    const ac = new AbortController();

    const fetchAll = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos_farmacia`, {
          credentials: 'include',
          signal: ac.signal
        });

        let sinGestionar = 0;
        let enFarmacia = 0;
        let enPreparacion = 0;
        let entregadoCadete = 0;
        let finalizados = 0;

        if (res.ok) {
          const json = await res.json().catch(() => ({}));
          const list = Array.isArray(json) ? json : (Array.isArray(json?.pedidos) ? json.pedidos : []);
          sinGestionar = list.filter((p) => p && p.estado === 'pendiente_call_center').length;
          enFarmacia = list.filter((p) => p && p.estado === 'pedido_generado_farmacia').length;
          enPreparacion = list.filter((p) => p && p.estado === 'en_preparacion').length;
          entregadoCadete = list.filter((p) => p && p.estado === 'en_camino').length;
          finalizados = list.filter((p) => p && (p.estado === 'entregado' || p.estado === 'cancelado')).length;
        }

        if (!mounted) return;
        setState({ loading: false, sinGestionar, enFarmacia, enPreparacion, entregadoCadete, finalizados });
      } catch (_) {
        if (mounted) setState((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchAll();
    const intervalId = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchAll();
    }, 30000);

    return () => {
      mounted = false;
      ac.abort();
      clearInterval(intervalId);
    };
  }, [enabled]);

  return state;
}

function useLogisticaKpis({ enabled = true } = {}) {
  const [state, setState] = useState({
    loading: true,
    enCamino: 0,
    entregados: 0,
    cancelados: 0
  });

  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, enCamino: 0, entregados: 0, cancelados: 0 });
      return undefined;
    }

    let mounted = true;
    const ac = new AbortController();

    const fetchAll = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos_farmacia`, {
          credentials: 'include',
          signal: ac.signal
        });

        let enCamino = 0;
        let entregados = 0;
        let cancelados = 0;

        if (res.ok) {
          const json = await res.json().catch(() => ({}));
          const list = Array.isArray(json) ? json : (Array.isArray(json?.pedidos) ? json.pedidos : []);
          enCamino = list.filter((p) => p && p.estado === 'en_camino').length;
          entregados = list.filter((p) => p && p.estado === 'entregado').length;
          cancelados = list.filter((p) => p && p.estado === 'cancelado').length;
        }

        if (!mounted) return;
        setState({ loading: false, enCamino, entregados, cancelados });
      } catch (_) {
        if (mounted) setState((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchAll();
    const intervalId = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchAll();
    }, 30000);

    return () => {
      mounted = false;
      ac.abort();
      clearInterval(intervalId);
    };
  }, [enabled]);

  return state;
}

function useRoleRecetasKpis({ enabled = true } = {}) {
  const [state, setState] = useState({ loading: true, data: { pedidos: [], recetas: [] } });

  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, data: { pedidos: [], recetas: [] } });
      return undefined;
    }

    let mounted = true;
    const ac = new AbortController();

    try {
      const cached = sessionStorage.getItem('recetasEmitirCacheV2');
      if (cached) {
        const parsed = JSON.parse(cached);
        const ageMs = Date.now() - Number(parsed?.at || 0);
        if (parsed?.data && ageMs >= 0 && ageMs <= 2 * 60 * 1000) {
          setState({ loading: false, data: parsed.data });
        }
      }
    } catch (_) {}

    const buildPlannedRecetas = (pedidosRaw) => {
      const pedidos = Array.isArray(pedidosRaw) ? pedidosRaw : [];
      const recetas = [];
      for (const p of pedidos) {
        if (!p || !p.id) continue;
        if (!['aprobado', 'en_tratamiento'].includes(String(p.estado))) continue;
        const cantidad = Number(p.cantidad_recetas) || 1;
        const fechaInicioRaw = p.fecha_inicio || p.fecha_solicitud || null;
        const fechaInicio = fechaInicioRaw ? new Date(fechaInicioRaw) : null;
        for (let i = 1; i <= cantidad; i += 1) {
          let fechaPrev = '';
          if (fechaInicio && !Number.isNaN(fechaInicio.getTime())) {
            const d = new Date(fechaInicio.getTime());
            d.setDate(d.getDate() + (i - 1) * 30);
            fechaPrev = d.toISOString().slice(0, 10);
          }
          recetas.push({
            id: `${p.id}-${i}`,
            pedido_id: p.id,
            numero: i,
            fecha_prevista: fechaPrev,
            estado: 'pendiente',
            nro_externo: ''
          });
        }
      }
      return { pedidos, recetas };
    };

    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos`, {
          credentials: 'include',
          signal: ac.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const pedidosRaw = Array.isArray(json?.pedidos) ? json.pedidos : (Array.isArray(json) ? json : []);
        const base = buildPlannedRecetas(pedidosRaw);
        if (mounted) setState({ loading: false, data: base });

        const targets = base.pedidos.filter((p) => p && p.id && ['aprobado', 'en_tratamiento'].includes(String(p.estado)));
        const persistedByPedido = {};
        const concurrency = 4;
        let cursor = 0;
        const worker = async () => {
          while (mounted) {
            const idx = cursor;
            cursor += 1;
            if (idx >= targets.length) return;
            const p = targets[idx];
            try {
              const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/${p.id}/recetas`, {
                credentials: 'include',
                signal: ac.signal
              });
              if (!r.ok) continue;
              const jr = await r.json().catch(() => ({}));
              const persisted = Array.isArray(jr?.recetas) ? jr.recetas : (Array.isArray(jr) ? jr : []);
              if (persisted.length) persistedByPedido[String(p.id)] = persisted;
            } catch (_) {}
          }
        };

        await Promise.all(Array.from({ length: concurrency }, worker));
        if (!mounted) return;

        const merged = base.recetas.map((rec) => {
          const persisted = persistedByPedido[String(rec.pedido_id)] || [];
          const match = persisted.find((pr) => Number(pr.numero_receta) === Number(rec.numero));
          if (!match) return rec;
          return {
            ...rec,
            estado: match.estado || rec.estado || 'pendiente',
            nro_externo: match.nro_receta_externo || rec.nro_externo || ''
          };
        });

        const next = { ...base, recetas: merged };
        setState({ loading: false, data: next });
        try { sessionStorage.setItem('recetasEmitirCacheV2', JSON.stringify({ at: Date.now(), data: next })); } catch (_) {}
      } catch (_) {
        if (mounted) setState((prev) => ({ loading: false, data: prev.data }));
      }
    })();

    return () => {
      mounted = false;
      ac.abort();
    };
  }, [enabled]);

  return state;
}

function Home() {
  const { user } = useAuth();
  const [linkedProfile, setLinkedProfile] = useState(null);
  const [showKpiLoading, setShowKpiLoading] = useState(true);
  const [showCurrentPartialMonth, setShowCurrentPartialMonth] = useState(false);
  const [kpiRangeDraft, setKpiRangeDraft] = useState(() => ({
    from: getDaysAgoDateValue(6),
    to: getTodayDateValue()
  }));
  const [kpiRangeApplied, setKpiRangeApplied] = useState(() => ({
    from: getDaysAgoDateValue(6),
    to: getTodayDateValue()
  }));
  useEffect(() => {
    if (!user) {
      setLinkedProfile(null);
      return undefined;
    }

    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/profile`, { credentials: 'include' });
        if (!res.ok || !mounted) return;
        const data = await res.json();
        if (mounted) setLinkedProfile(data);
      } catch (_) {
        /* perfil opcional para saludo */
      }
    })();

    return () => {
      mounted = false;
    };
  }, [user?.id, user?.username]);

  const isAdmin = Boolean(user?.isAdmin);
  const canSeeMedicoKpis = useMemo(() => {
    if (isAdmin) return false;
    const perms = Array.isArray(user?.permissions) ? user.permissions : [];
    const rawName = String(user?.name || user?.username || '').toLowerCase();
    if (rawName.includes('medic') || rawName.includes('doctor') || rawName.includes('dr.')) return true;
    return perms.includes('ver_pedido_recetas_medico');
  }, [isAdmin, user?.permissions, user?.name, user?.username]);

  const canSeeRecetasKpis = useMemo(() => {
    if (isAdmin || canSeeMedicoKpis) return false;
    const perms = Array.isArray(user?.permissions) ? user.permissions : [];
    return perms.includes('ver_recetas_emitir') || perms.includes('ver_recetas_cruzadas');
  }, [isAdmin, canSeeMedicoKpis, user?.permissions]);

  const canSeeLogisticaKpis = useMemo(() => {
    if (isAdmin || canSeeRecetasKpis || canSeeMedicoKpis) return false;
    const perms = Array.isArray(user?.permissions) ? user.permissions : [];
    const rawName = String(user?.name || user?.username || '').toLowerCase();
    if (rawName.includes('logistic') || rawName.includes('logística') || rawName.includes('cadeteria') || rawName.includes('cadetería')) return true;
    const hasPedidos = perms.includes('ver_pedidos');
    const hasCallCenterSignals = perms.includes('ver_conversaciones')
      || perms.includes('ver_contactos')
      || perms.includes('ver_pedidos_dispensa');
    return hasPedidos && !hasCallCenterSignals;
  }, [isAdmin, canSeeRecetasKpis, canSeeMedicoKpis, user?.permissions, user?.name, user?.username]);

  const canSeeCallCenterKpis = useMemo(() => {
    if (isAdmin || canSeeRecetasKpis || canSeeMedicoKpis || canSeeLogisticaKpis) return false;
    const perms = Array.isArray(user?.permissions) ? user.permissions : [];
    const rawName = String(user?.name || user?.username || '').toLowerCase();
    if (rawName.includes('callcenter') || rawName.includes('call center') || rawName.includes('call_center')) return true;
    return perms.includes('ver_conversaciones') || perms.includes('ver_contactos') || perms.includes('ver_pedidos_dispensa');
  }, [isAdmin, canSeeRecetasKpis, canSeeMedicoKpis, canSeeLogisticaKpis, user?.permissions, user?.name, user?.username]);

  const roleKpisState = useRoleRecetasKpis({ enabled: canSeeRecetasKpis || canSeeMedicoKpis });
  const callCenterKpis = useCallCenterKpis({ enabled: canSeeCallCenterKpis });
  const logisticaKpis = useLogisticaKpis({ enabled: canSeeLogisticaKpis });
  const medicoKpis = useMemo(() => {
    const pedidos = roleKpisState.data.pedidos || [];
    return {
      pendientes: pedidos.filter((p) => p && p.estado === 'pendiente_aprobacion_medica').length,
      aprobados: pedidos.filter((p) => p && p.estado === 'aprobado').length,
      rechazados: pedidos.filter((p) => p && p.estado === 'rechazado').length,
      enTratamiento: pedidos.filter((p) => p && p.estado === 'en_tratamiento').length
    };
  }, [roleKpisState.data]);
  const roleKpis = useMemo(() => {
    const recetas = roleKpisState.data.recetas || [];
    const pedidos = roleKpisState.data.pedidos || [];
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const min = new Date(hoy.getTime()); min.setDate(min.getDate() - 7);
    const max = new Date(hoy.getTime()); max.setDate(max.getDate() + 7);
    const enVentana = (fechaPrevista) => {
      if (!fechaPrevista) return false;
      const d = new Date(`${fechaPrevista}T00:00:00`);
      if (Number.isNaN(d.getTime())) return false;
      d.setHours(0, 0, 0, 0);
      return d >= min && d <= max;
    };
    const pendientes = recetas.filter((r) => r.estado === 'pendiente' && enVentana(r.fecha_prevista)).length;
    const emitidas = recetas.filter((r) => r.estado === 'emitida_sistema_externo' || r.estado === 'confirmada_dispensa').length;
    const totalRecetas = recetas.length;
    const pedidosActivosConEmitidas = new Set(
      recetas
        .filter((r) => r.estado === 'emitida_sistema_externo' || r.estado === 'confirmada_dispensa')
        .map((r) => String(r.pedido_id))
    );
    const tratamientosActivos = pedidos.filter((p) => pedidosActivosConEmitidas.has(String(p.id))).length;
    return { pendientes, emitidas, totalRecetas, tratamientosActivos };
  }, [roleKpisState.data]);

  const { data: response, loading } = useApi('/api/asociacion?page=1&limit=1');
  const { data: recetasResponse, loading: recetasLoading } = useApi('/api/asociacion-recetas?page=1&limit=1');
  const recetasCruceEndpoint = useMemo(() => {
    const params = new URLSearchParams();
    if (kpiRangeApplied.from) params.set('from', kpiRangeApplied.from);
    if (kpiRangeApplied.to) params.set('to', kpiRangeApplied.to);
    return `/api/asociacion/recetas-cruce-kpis?${params.toString()}`;
  }, [kpiRangeApplied.from, kpiRangeApplied.to]);
  const { data: recetasCruceKpisResponse, loading: recetasCruceKpisLoading } = useApi(recetasCruceEndpoint);
  const { data: monthlyRecetasResponse, loading: monthlyLoading } = useApi(
    '/api/asociacion-recetas/monthly-stats?months=7&includeCurrent=1'
  );

  const total = response?.total ?? 0;
  const stats = response?.stats ?? {};
  const totalCruzados = stats.totalCruzados ?? 0;
  const ultFarmacia = stats.ultFarmacia ?? 0;
  const ultSanatorio = stats.ultSanatorio ?? 0;
  const conTelefonoOCelGeclisa = stats.conTelefonoOCelGeclisa ?? 0;
  const conTelefonoPlex = stats.conTelefonoPlex ?? 0;
  const conAlgunContacto = stats.conAlgunContacto ?? 0;

  const recetasTotal = recetasResponse?.total ?? 0;
  const recetasStats = recetasResponse?.stats ?? {};
  const recetasEmitidasSanatorio = recetasStats.emitidasSanatorio ?? recetasTotal;
  const recetasConIdentificador = recetasStats.conIdentificador ?? 0;
  const recetasCruceConPlex = recetasStats.cruceConPlex ?? 0;
  const recetasSinCruceValidada = recetasStats.sinCruceValidada ?? 0;
  const recetasSinIdentificador = Math.max(
    recetasEmitidasSanatorio - recetasConIdentificador,
    0
  );

  useEffect(() => {
    if (loading || recetasLoading) {
      setShowKpiLoading(true);
      return;
    }

    const timeout = setTimeout(() => {
      setShowKpiLoading(false);
    }, 900);

    return () => clearTimeout(timeout);
  }, [loading, recetasLoading]);

  const recetasCruceKpis = useMemo(() => {
    const emptyBlock = {
      date: null,
      totalRegistros: 0,
      totalMatch: 0,
      soloPlex: 0,
      soloGeclisa: 0,
      sinDatos: 0,
      porcentajeMatch: 0
    };
    const bestWeekday = recetasCruceKpisResponse?.bestWeekday || null;
    const worstWeekday = recetasCruceKpisResponse?.worstWeekday || null;
    const recentDays = Array.isArray(recetasCruceKpisResponse?.recentDays)
      ? recetasCruceKpisResponse.recentDays
      : [];
    const weekdayStats = Array.isArray(recetasCruceKpisResponse?.weekdayStats)
      ? recetasCruceKpisResponse.weekdayStats
      : [];
    const activeRangeDays = recentDays.filter((item) => Number(item?.totalRegistros || 0) > 0);
    const totalRangeRegistros = activeRangeDays.reduce((acc, item) => acc + Number(item.totalRegistros || 0), 0);
    const totalRangeMatch = activeRangeDays.reduce((acc, item) => acc + Number(item.totalMatch || 0), 0);
    const totalRangeSoloPlex = activeRangeDays.reduce((acc, item) => acc + Number(item.soloPlex || 0), 0);
    const totalRangeSoloGeclisa = activeRangeDays.reduce((acc, item) => acc + Number(item.soloGeclisa || 0), 0);
    const totalRangeSinDatos = activeRangeDays.reduce((acc, item) => acc + Number(item.sinDatos || 0), 0);
    const averageDailyVolumeRange = activeRangeDays.length ? totalRangeRegistros / activeRangeDays.length : 0;
    const averageDailyMatchRange = activeRangeDays.length ? totalRangeMatch / activeRangeDays.length : 0;
    const zeroMatchDaysRange = activeRangeDays.filter((item) => Number(item.totalMatch || 0) === 0).length;
    const daysWithAnyMatch = activeRangeDays.filter((item) => Number(item.totalMatch || 0) > 0).length;
    const today = recetasCruceKpisResponse?.currentDay || emptyBlock;
    const yesterday = recetasCruceKpisResponse?.previousDay || emptyBlock;
    const todayVsYesterdayPoints = Number(today.porcentajeMatch || 0) - Number(yesterday.porcentajeMatch || 0);
    const peakVolumeDay = activeRangeDays.length
      ? activeRangeDays.reduce((peak, item) => (Number(item.totalRegistros || 0) > Number(peak.totalRegistros || 0) ? item : peak), activeRangeDays[0])
      : null;
    const peakMatchDay = activeRangeDays.length
      ? activeRangeDays.reduce((peak, item) => (Number(item.totalMatch || 0) > Number(peak.totalMatch || 0) ? item : peak), activeRangeDays[0])
      : null;
    const consistencyPercent = activeRangeDays.length
      ? Number(((daysWithAnyMatch * 100) / activeRangeDays.length).toFixed(2))
      : 0;
    const unmatchedRange = Math.max(totalRangeRegistros - totalRangeMatch, 0);
    const qualityGap = totalRangeSoloGeclisa + totalRangeSoloPlex + totalRangeSinDatos;

    return {
      currentDay: today,
      previousDay: yesterday,
      rolling7Days: recetasCruceKpisResponse?.rolling7Days || emptyBlock,
      monthToDate: recetasCruceKpisResponse?.monthToDate || emptyBlock,
      period: recetasCruceKpisResponse?.period || { daysWindow: 30, from: null, to: null },
      bestWeekday,
      worstWeekday,
      recentDays,
      weekdayStats,
      averageDailyVolumeRange,
      averageDailyMatchRange,
      zeroMatchDaysRange,
      activeDaysRange: activeRangeDays.length,
      todayVsYesterdayPoints,
      totalRangeRegistros,
      totalRangeMatch,
      totalRangeSoloPlex,
      totalRangeSoloGeclisa,
      totalRangeSinDatos,
      peakVolumeDay,
      peakMatchDay,
      consistencyPercent,
      daysWithAnyMatch,
      unmatchedRange,
      qualityGap
    };
  }, [recetasCruceKpisResponse]);

  const appliedRangeDays = useMemo(
    () => diffDaysInclusive(kpiRangeApplied.from, kpiRangeApplied.to),
    [kpiRangeApplied.from, kpiRangeApplied.to]
  );

  const handleKpiRangeDraftChange = (field) => (event) => {
    const value = event.target.value;
    setKpiRangeDraft((prev) => ({ ...prev, [field]: value }));
  };

  const applyKpiRange = () => {
    if (!kpiRangeDraft.from || !kpiRangeDraft.to) return;
    if (kpiRangeDraft.from > kpiRangeDraft.to) return;
    setKpiRangeApplied(kpiRangeDraft);
  };

  const applyKpiPreset = (preset) => {
    const nextRange = preset === '7d'
      ? { from: getDaysAgoDateValue(6), to: getTodayDateValue() }
      : preset === '30d'
        ? { from: getDaysAgoDateValue(29), to: getTodayDateValue() }
        : { from: getMonthStartDateValue(), to: getTodayDateValue() };

    setKpiRangeDraft(nextRange);
    setKpiRangeApplied(nextRange);
  };

  const lastClosureLabel = useMemo(() => {
    if (!recetasCruceKpis.currentDay?.date) return 'Último cierre';
    return recetasCruceKpis.currentDay.date === getTodayDateValue() ? 'Hoy' : 'Último cierre';
  }, [recetasCruceKpis.currentDay?.date]);

  const previousClosureLabel = useMemo(() => {
    if (!recetasCruceKpis.previousDay?.date) return 'Jornada previa';
    return recetasCruceKpis.previousDay.date === getDaysAgoDateValue(1) ? 'Ayer' : 'Jornada previa';
  }, [recetasCruceKpis.previousDay?.date]);

  const displayName = useMemo(
    () => resolveHomeDisplayName(user, linkedProfile),
    [user, linkedProfile]
  );
  const roleName = useMemo(() => {
    if (isAdmin) return 'Admin';
    const perms = Array.isArray(user?.permissions) ? user.permissions : [];
    const rawName = String(user?.name || user?.username || '').toLowerCase();
    if (rawName.includes('administrativo')) return 'Administrativo';
    if (rawName.includes('medic') || rawName.includes('doctor') || rawName.includes('dr.')) return 'Médico';
    if (rawName.includes('cliente')) return 'Cliente';
    if (rawName.includes('callcenter') || rawName.includes('call center')) return 'Call Center';
    if (rawName.includes('logistica') || rawName.includes('logística')) return 'Logística';
    if (rawName.includes('farmacia')) return 'Farmacia';
    if (perms.some((p) => String(p).toLowerCase().includes('admin'))) return 'Administrativo';
    if (perms.includes('ver_pedido_recetas_medico')) return 'Médico';
    if (perms.includes('ver_conversaciones') && perms.includes('ver_contactos')) return 'Call Center';
    if (perms.includes('ver_pedidos') || perms.includes('ver_pedidos_dispensa')) return 'Operaciones';
    if (perms.includes('ver_asociacion') || perms.includes('ver_recetas')) return 'Analista';
    if (perms.includes('ver_conversaciones')) return 'Atención';
    return 'Usuario';
  }, [isAdmin, user?.permissions]);
  const suggestedLinks = useMemo(() => {
    const perms = Array.isArray(user?.permissions) ? user.permissions : [];
    const linksCatalog = [
      { to: '/contact-center', icon: '💊', title: 'Contact Center', description: 'Dispensa, farmacia y estados del pedido.', accentLabel: 'Contact Center', permission: 'ver_pedidos_dispensa' },
      { to: '/bitacora-operativa', icon: '◈', title: 'Trazabilidad', description: 'Registro auditado de movimientos y estados.', accentLabel: 'Auditoría', permission: 'ver_pulse_ledger' },
      { to: '/delivery', icon: '🏍️', title: 'Última milla', description: 'Seguimiento de pedidos en camino.', accentLabel: 'Última milla', permission: 'ver_pedidos' },
      { to: '/bot-farmacia', icon: '🤖', title: 'Bot Farmacia', description: 'Atención mostrador — simulación local.', accentLabel: 'Bot', permission: 'ver_bot_farmacia' },
      { to: '/pedidos-recetas-medico', icon: '🩺', title: 'Pedidos/Recetas', description: 'Flujo médico y operativa de pedidos.', accentLabel: 'Médico', permission: 'ver_pedido_recetas_medico' },
      { to: '/recetas-cruzadas', icon: '🧾', title: 'Recetas cruzadas', description: 'Trazabilidad de cruces y validaciones.', accentLabel: 'Recetas', permission: 'ver_recetas_cruzadas' },
      { to: '/recetas-emitir', icon: '📄', title: 'Recetas a emitir', description: 'Cola de emisión pendiente.', accentLabel: 'Emisión', permission: 'ver_recetas_emitir' },
      { to: '/conversaciones', icon: '💬', title: 'Conversaciones', description: 'Atención y seguimiento de chats.', accentLabel: 'Atención', permission: 'ver_conversaciones' },
      { to: '/asociacion-clientes', icon: '👥', title: 'Contactos', description: 'Vista de pacientes y contacto.', accentLabel: 'Clientes', permission: 'ver_contactos' },
    ];

    const byPermission = linksCatalog.filter((it) => perms.includes(it.permission));
    if (byPermission.length > 0) return byPermission.slice(0, 6);
    return [{
      to: '/',
      icon: '🏠',
      title: 'Inicio',
      description: 'No hay permisos funcionales asignados para accesos rápidos.',
      accentLabel: 'Dashboard'
    }];
  }, [user?.permissions]);
  const nowText = useMemo(
    () =>
      new Date().toLocaleString('es-AR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      }),
    []
  );

  const derived = useMemo(() => {
    const pacientesSinCruce = Math.max(total - totalCruzados, 0);
    const tasaCrucePacientes = getPercent(totalCruzados, total);
    const tasaIdentificador = getPercent(recetasConIdentificador, recetasEmitidasSanatorio);
    const tasaCruceRecetas = getPercent(recetasCruceConPlex, recetasEmitidasSanatorio);
    const tasaPendienteRecetas = getPercent(recetasSinCruceValidada, recetasEmitidasSanatorio);
    const tasaTelefonoGeclisa = getPercent(conTelefonoOCelGeclisa, totalCruzados);
    const tasaTelefonoPlex = getPercent(conTelefonoPlex, totalCruzados);
    const tasaAlgunContacto = getPercent(conAlgunContacto, totalCruzados);
    const farmaciaDominante = ultFarmacia >= ultSanatorio;
    const score = Math.round(
      tasaCrucePacientes * 0.35 +
        tasaAlgunContacto * 0.2 +
        tasaIdentificador * 0.2 +
        tasaCruceRecetas * 0.25
    );

    return {
      pacientesSinCruce,
      tasaCrucePacientes,
      tasaIdentificador,
      tasaCruceRecetas,
      tasaPendienteRecetas,
      tasaTelefonoGeclisa,
      tasaTelefonoPlex,
      tasaAlgunContacto,
      farmaciaDominante,
      score,
      scoreMeta: getScoreMeta(score)
    };
  }, [
    conAlgunContacto,
    conTelefonoOCelGeclisa,
    conTelefonoPlex,
    recetasConIdentificador,
    recetasCruceConPlex,
    recetasEmitidasSanatorio,
    recetasSinCruceValidada,
    total,
    totalCruzados,
    ultFarmacia,
    ultSanatorio
  ]);

  const rollingMonthsWindow = useMemo(
    () => monthlyRecetasResponse?.monthsWindow || 6,
    [monthlyRecetasResponse?.monthsWindow]
  );

  const monthlySeriesData = useMemo(() => {
    const rawMonths = Array.isArray(monthlyRecetasResponse?.months)
      ? monthlyRecetasResponse.months
      : [];
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    return rawMonths.map((item) => ({
      year: item.year,
      month: item.month,
      isCurrentMonth: Number(item.month) === currentMonth && Number(item.year) === currentYear,
      label: formatMonthYearLabel(item.month, item.year),
      fullLabel: formatMonthYearFullLabel(item.month, item.year),
      conIdentificador: item.conIdentificador || 0,
      conCruce: item.conCruce || 0,
      sinCruceValidada: item.sinCruceValidada || 0
    }));
  }, [monthlyRecetasResponse]);

  const hasCurrentMonthInSeries = useMemo(
    () => monthlySeriesData.some((item) => item.isCurrentMonth),
    [monthlySeriesData]
  );

  const visibleMonthlySeriesData = useMemo(() => {
    if (showCurrentPartialMonth) return monthlySeriesData;
    return monthlySeriesData.filter((item) => !item.isCurrentMonth);
  }, [monthlySeriesData, showCurrentPartialMonth]);

  const monthlyLabels = useMemo(
    () => visibleMonthlySeriesData.map((item) => (item.isCurrentMonth ? `${item.label}*` : item.label)),
    [visibleMonthlySeriesData]
  );

  const monthlyFullLabels = useMemo(
    () => visibleMonthlySeriesData.map((item) => (item.isCurrentMonth ? `${item.fullLabel} (parcial)` : item.fullLabel)),
    [visibleMonthlySeriesData]
  );

  const monthlyChartSeries = useMemo(
    () => [
      {
        key: 'identificador',
        label: 'Con identificador',
        color: '#2563EB',
        data: visibleMonthlySeriesData.map((item) => item.conIdentificador)
      },
      {
        key: 'cruce',
        label: 'Cruce confirmado con PLEX',
        color: '#16A34A',
        data: visibleMonthlySeriesData.map((item) => item.conCruce)
      },
      {
        key: 'sinCruce',
        label: 'Validadas sin match',
        color: '#EA580C',
        data: visibleMonthlySeriesData.map((item) => item.sinCruceValidada)
      }
    ],
    [visibleMonthlySeriesData]
  );

  const monthlyChartTitle = showCurrentPartialMonth && hasCurrentMonthInSeries
    ? `Recetas últimos 6 meses + mes en curso (parcial)`
    : 'Recetas últimos 6 meses completos';

  if (!isAdmin) {
    return (
      <div style={{ ...styles.container, maxWidth: 'none', width: '100%' }}>
        <section style={styles.heroPanel} className="home-hero-panel home-role-hero-panel">
          <div style={styles.heroMain} className="home-hero-main">
            <div style={styles.heroEyebrow}>Grupo Bienestar</div>
            <h2 style={styles.heroTitle}>{`Dashboard de ${roleName}`}</h2>
            <p style={styles.heroDescription}>
              Vista simplificada para tu perfil. El tablero completo queda reservado para administradores.
            </p>
            <div style={styles.heroPills} className="home-hero-pills home-role-pills">
              <div style={{ ...styles.balanceCard, minWidth: 150, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: 'rgba(255,255,255,0.7)', fontWeight: 700 }}>Rol activo</div>
                <div style={{ marginTop: 6, color: '#fff', fontWeight: 800, fontSize: 20 }}>{roleName}</div>
              </div>
              <div style={{ ...styles.balanceCard, minWidth: 150, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: 'rgba(255,255,255,0.7)', fontWeight: 700 }}>Estado</div>
                <div style={{ marginTop: 6, color: '#fff', fontWeight: 800, fontSize: 20 }}>Operativo</div>
              </div>
            </div>
          </div>
          <div style={styles.heroProfileCard} className="home-hero-score-card">
            <div style={styles.heroProfileTop}>
              <img src={HOME_ASSISTANT_AVATAR} alt="Anita" style={styles.heroProfileAvatar} />
              <div style={styles.heroProfileIdentity}>
                <div style={styles.heroProfileEyebrow}>Grupo Bienestar Assistant</div>
                <div style={styles.heroProfileName}>{displayName}</div>
              </div>
            </div>
            <div style={styles.heroProfileDescription}>
              {`Bienvenido${displayName ? `, ${displayName}` : ''}. Esta vista prioriza accesos rápidos según tu rol.`}
            </div>
            <div style={styles.heroProfileMeta}>
              <span style={styles.heroProfileMetaLabel}>Ultima actualización</span>
              <strong style={styles.heroProfileMetaValue}>{nowText}</strong>
            </div>
          </div>
        </section>

        {canSeeMedicoKpis ? (
          <section style={styles.insightGrid} className="home-insight-grid">
            <RoleKpiCard
              eyebrow="Pendientes"
              value={medicoKpis.pendientes}
              hint="Pedidos esperando aprobación médica."
              loading={roleKpisState.loading}
              accent="#F59E0B"
            />
            <RoleKpiCard
              eyebrow="Aprobados hoy"
              value={medicoKpis.aprobados}
              hint="Pedidos aprobados listos para tratamiento."
              loading={roleKpisState.loading}
              accent="#10B981"
            />
            <RoleKpiCard
              eyebrow="Rechazados"
              value={medicoKpis.rechazados}
              hint="Pedidos rechazados por el médico."
              loading={roleKpisState.loading}
              accent="#EF4444"
            />
            <RoleKpiCard
              eyebrow="En tratamiento"
              value={medicoKpis.enTratamiento}
              hint="Pacientes con tratamiento activo en curso."
              loading={roleKpisState.loading}
              accent="#2365AB"
            />
          </section>
        ) : canSeeRecetasKpis ? (
          <section style={styles.insightGrid} className="home-insight-grid">
            <RoleKpiCard
              eyebrow="Pendientes emisión"
              value={roleKpis.pendientes}
              hint="Recetas dentro de la ventana operativa de 7 días."
              loading={roleKpisState.loading}
              accent="#F59E0B"
            />
            <RoleKpiCard
              eyebrow="Emitidas"
              value={roleKpis.emitidas}
              hint="Recetas con número externo confirmado."
              loading={roleKpisState.loading}
              accent="#10B981"
            />
            <RoleKpiCard
              eyebrow="Total recetas"
              value={roleKpis.totalRecetas}
              hint="Plan completo de recetas para tratamientos activos."
              loading={roleKpisState.loading}
              accent="#2365AB"
            />
            <RoleKpiCard
              eyebrow="Tratamientos activos"
              value={roleKpis.tratamientosActivos}
              hint="Pedidos en curso con al menos una receta emitida."
              loading={roleKpisState.loading}
              accent="#8B5CF6"
            />
          </section>
        ) : canSeeCallCenterKpis ? (
          <section style={styles.insightGrid} className="home-insight-grid">
            <RoleKpiCard
              eyebrow="Sin gestión"
              value={callCenterKpis.sinGestionar}
              hint="Pedidos pendientes de gestión por call center."
              loading={callCenterKpis.loading}
              accent="#EF4444"
            />
            <RoleKpiCard
              eyebrow="En farmacia"
              value={callCenterKpis.enFarmacia}
              hint="Pedido generado en farmacia."
              loading={callCenterKpis.loading}
              accent="#F59E0B"
            />
            <RoleKpiCard
              eyebrow="En preparación"
              value={callCenterKpis.enPreparacion}
              hint="Solo lectura, actualiza Farmacia."
              loading={callCenterKpis.loading}
              accent="#FB923C"
            />
            <RoleKpiCard
              eyebrow="Entregado al cadete"
              value={callCenterKpis.entregadoCadete}
              hint="Solo lectura, actualizado por Farmacia."
              loading={callCenterKpis.loading}
              accent="#2365AB"
            />
            <RoleKpiCard
              eyebrow="Finalizados"
              value={callCenterKpis.finalizados}
              hint="Entregados o cancelados."
              loading={callCenterKpis.loading}
              accent="#10B981"
            />
          </section>
        ) : canSeeLogisticaKpis ? (
          <section style={styles.insightGrid} className="home-insight-grid">
            <RoleKpiCard
              eyebrow="En camino"
              value={logisticaKpis.enCamino}
              hint="Pedidos despachados en tránsito hacia el paciente."
              loading={logisticaKpis.loading}
              accent="#2365AB"
            />
            <RoleKpiCard
              eyebrow="Entregados"
              value={logisticaKpis.entregados}
              hint="Pedidos finalizados con entrega confirmada."
              loading={logisticaKpis.loading}
              accent="#10B981"
            />
            <RoleKpiCard
              eyebrow="Cancelados"
              value={logisticaKpis.cancelados}
              hint="Pedidos finalizados como cancelados."
              loading={logisticaKpis.loading}
              accent="#EF4444"
            />
          </section>
        ) : (
          <section style={styles.insightGrid} className="home-insight-grid">
            <div style={styles.balanceCard}>
              <div style={styles.panelEyebrow}>Rol</div>
              <div style={{ marginTop: 8, fontSize: 28, fontWeight: 800, color: CRM.navy }}>{roleName}</div>
              <div style={{ marginTop: 8, fontSize: 13, color: CRM.textLight }}>Perfil de acceso detectado para navegación guiada.</div>
            </div>
            <div style={styles.balanceCard}>
              <div style={styles.panelEyebrow}>Prioridad</div>
              <div style={{ marginTop: 8, fontSize: 28, fontWeight: 800, color: CRM.navy }}>Activa</div>
              <div style={{ marginTop: 8, fontSize: 13, color: CRM.textLight }}>Tus pantallas principales están listas para operar.</div>
            </div>
            <div style={styles.balanceCard}>
              <div style={styles.panelEyebrow}>Modo</div>
              <div style={{ marginTop: 8, fontSize: 28, fontWeight: 800, color: CRM.navy }}>On</div>
              <div style={{ marginTop: 8, fontSize: 13, color: CRM.textLight }}>Vista resumida para usuarios no administradores.</div>
            </div>
          </section>
        )}

        <section style={styles.panel} className="home-panel">
          <div style={styles.panelHeader} className="home-panel-header">
            <div>
              <div style={styles.panelEyebrow}>Acceso rápido por rol</div>
              <h3 style={styles.panelTitle}>Entradas sugeridas</h3>
            </div>
            <div style={styles.panelTag}>{roleName}</div>
          </div>
          <div style={styles.quickLinksGrid} className="home-quick-links-grid">
            {suggestedLinks.map((link) => (
              <QuickLinkCard
                key={`${link.to}-${link.title}`}
                to={link.to}
                icon={link.icon}
                title={link.title}
                description={link.description}
                accentLabel={link.accentLabel}
              />
            ))}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {showKpiLoading ? (
        <section style={styles.kpis}>
          <HomeKpiSyncLoader />
        </section>
      ) : (
        <>
          <section style={styles.heroPanel} className="home-hero-panel">
            <div style={styles.heroMain} className="home-hero-main">
              <div style={styles.heroEyebrow}>Grupo Bienestar Control Center</div>
              <h2 style={styles.heroTitle}>{derived.scoreMeta.label}</h2>
              <p style={styles.heroDescription}>{derived.scoreMeta.description}</p>

              <div style={styles.heroPills} className="home-hero-pills">
                <StatPill
                  label="Cruce pacientes"
                  value={`${derived.tasaCrucePacientes.toFixed(2)}%`}
                  tone={getTone(derived.tasaCrucePacientes)}
                />
                <StatPill
                  label="Cruce recetas"
                  value={formatPercent(derived.tasaCruceRecetas)}
                  tone={getTone(derived.tasaCruceRecetas)}
                />
              </div>
            </div>

            <div style={styles.heroProfileCard} className="home-hero-score-card">
              <div style={styles.heroProfileTop}>
                <img src={HOME_ASSISTANT_AVATAR} alt="Anita" style={styles.heroProfileAvatar} />
                <div style={styles.heroProfileIdentity}>
                  <div style={styles.heroProfileEyebrow}>Grupo Bienestar Assistant</div>
                  <div style={styles.heroProfileName}>{displayName}</div>
                </div>
              </div>
              <div style={styles.heroProfileDescription}>
                <strong style={styles.heroProfileDescriptionLead}>
                  {`Bienvenido${displayName ? `, ${displayName}` : ' a Grupo Bienestar'}.`}
                </strong>
                {' '}
                Centro de comando asociativo PLEX ↔ GECLISA.
              </div>
              <div style={styles.heroProfileMeta}>
                <span style={styles.heroProfileMetaLabel}>Snapshot operativo</span>
                <strong style={styles.heroProfileMetaValue}>{nowText}</strong>
              </div>
            </div>
          </section>

          <section style={styles.insightGrid} className="home-insight-grid">
            <InsightMetric
              eyebrow="Pacientes"
              title="Universo asociado"
              value={formatNumber(total)}
              hint={`${formatNumber(totalCruzados)} ya están cruzados entre ambos sistemas`}
              tone="navy"
            />
            <InsightMetric
              eyebrow="Oportunidad"
              title="Pendientes de cruce"
              value={formatNumber(derived.pacientesSinCruce)}
              hint="Registros que todavía no consolidan identidad completa"
              tone="amber"
            />
            <InsightMetric
              eyebrow="Recetas"
              title="Emitidas por sanatorio"
              value={formatNumber(recetasEmitidasSanatorio)}
              hint={`${formatNumber(recetasCruceConPlex)} ya tienen match efectivo con PLEX`}
              tone="emerald"
            />
            <InsightMetric
              eyebrow="Pendiente"
              title="Sin identificador"
              value={formatNumber(recetasSinIdentificador)}
              hint="Principal espacio de mejora para la trazabilidad"
              tone="rose"
            />
          </section>

          <section style={styles.panel} className="home-panel">
            <div style={styles.panelHeader} className="home-panel-header">
              <div>
                <div style={styles.panelEyebrow}>Cruces de recetas</div>
                <h3 style={styles.panelTitle}>KPIs diarios de match GECLISA ↔ PLEX</h3>
              </div>
              <div style={styles.panelTag}>{`${appliedRangeDays} días`}</div>
            </div>

            <div
              style={{
                display: 'grid',
                gap: 16,
                marginBottom: 18,
                padding: '16px 18px',
                borderRadius: 18,
                border: '1px solid #D8E2EE',
                background: 'linear-gradient(135deg, #F8FBFF 0%, #EEF5FB 100%)'
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: CRM.textXlight }}>
                    Rango analítico
                  </div>
                  <div style={{ marginTop: 6, fontSize: 15, fontWeight: 700, color: CRM.navy }}>
                    {`${formatLongDate(kpiRangeApplied.from)} al ${formatLongDate(kpiRangeApplied.to)}`}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12, color: CRM.textLight }}>
                    Elegí cualquier período y compará el comportamiento diario del cruce de recetas.
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {[
                    { key: 'mtd', label: 'Mes actual' },
                    { key: '7d', label: '7 días' },
                    { key: '30d', label: '30 días' }
                  ].map((preset) => (
                    <button
                      key={preset.key}
                      type="button"
                      onClick={() => applyKpiPreset(preset.key)}
                      style={{
                        border: '1px solid #C8D6E5',
                        background: '#FFFFFF',
                        color: CRM.navy,
                        borderRadius: 999,
                        padding: '8px 12px',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, alignItems: 'end' }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: CRM.text }}>Desde</span>
                  <input
                    type="date"
                    value={kpiRangeDraft.from}
                    max={kpiRangeDraft.to || getTodayDateValue()}
                    onChange={handleKpiRangeDraftChange('from')}
                    style={{
                      borderRadius: 12,
                      border: '1px solid #C8D6E5',
                      padding: '11px 12px',
                      fontSize: 14,
                      color: CRM.navy,
                      background: '#FFFFFF'
                    }}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: CRM.text }}>Hasta</span>
                  <input
                    type="date"
                    value={kpiRangeDraft.to}
                    min={kpiRangeDraft.from}
                    max={getTodayDateValue()}
                    onChange={handleKpiRangeDraftChange('to')}
                    style={{
                      borderRadius: 12,
                      border: '1px solid #C8D6E5',
                      padding: '11px 12px',
                      fontSize: 14,
                      color: CRM.navy,
                      background: '#FFFFFF'
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={applyKpiRange}
                  disabled={!kpiRangeDraft.from || !kpiRangeDraft.to || kpiRangeDraft.from > kpiRangeDraft.to}
                  style={{
                    border: 'none',
                    borderRadius: 12,
                    padding: '12px 16px',
                    background: '#2365AB',
                    color: '#FFFFFF',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: !kpiRangeDraft.from || !kpiRangeDraft.to || kpiRangeDraft.from > kpiRangeDraft.to ? 'not-allowed' : 'pointer',
                    opacity: !kpiRangeDraft.from || !kpiRangeDraft.to || kpiRangeDraft.from > kpiRangeDraft.to ? 0.55 : 1
                  }}
                >
                  Aplicar rango
                </button>
              </div>
            </div>

            <div style={styles.insightGrid} className="home-insight-grid">
              <InsightMetric
                eyebrow={lastClosureLabel}
                title="Match del día"
                value={formatPrecisePercent(recetasCruceKpis.currentDay.porcentajeMatch)}
                hint={`${formatNumber(recetasCruceKpis.currentDay.totalMatch)} matches sobre ${formatNumber(recetasCruceKpis.currentDay.totalRegistros)} registros del ${formatShortDate(recetasCruceKpis.currentDay.date)}`}
                tone="emerald"
              />
              <InsightMetric
                eyebrow={previousClosureLabel}
                title="Comparativo inmediato"
                value={formatPrecisePercent(recetasCruceKpis.previousDay.porcentajeMatch)}
                hint={`${formatNumber(recetasCruceKpis.previousDay.totalMatch)} matches sobre ${formatNumber(recetasCruceKpis.previousDay.totalRegistros)} registros del ${formatShortDate(recetasCruceKpis.previousDay.date)}`}
                tone="navy"
              />
              <InsightMetric
                eyebrow="Últimos 7 días"
                title="Rolling semanal"
                value={formatPrecisePercent(recetasCruceKpis.rolling7Days.porcentajeMatch)}
                hint={`${formatNumber(recetasCruceKpis.rolling7Days.totalMatch)} matches sobre ${formatNumber(recetasCruceKpis.rolling7Days.totalRegistros)} recetas del período`}
                tone="amber"
              />
              <InsightMetric
                eyebrow="Mes en curso"
                title="Month to date"
                value={formatPrecisePercent(recetasCruceKpis.monthToDate.porcentajeMatch)}
                hint={`${formatNumber(recetasCruceKpis.monthToDate.totalMatch)} matches sobre ${formatNumber(recetasCruceKpis.monthToDate.totalRegistros)} registros acumulados`}
                tone="rose"
              />
            </div>

            <div style={styles.balanceGrid} className="home-balance-grid">
              <div style={styles.balanceCard}>
                <span style={styles.balanceLabel}>Mejor día semanal</span>
                <strong style={styles.balanceValue}>{recetasCruceKpis.bestWeekday?.diaSemana || 'Sin datos'}</strong>
                <span style={styles.balanceHint}>
                  {recetasCruceKpis.bestWeekday
                    ? `${formatPrecisePercent(recetasCruceKpis.bestWeekday.porcentajeMatch)} de match sobre ${formatNumber(recetasCruceKpis.bestWeekday.totalRegistros)} registros`
                    : 'Todavía no hay volumen suficiente en la ventana elegida'}
                </span>
              </div>
              <div style={styles.balanceCard}>
                <span style={styles.balanceLabel}>Peor día semanal</span>
                <strong style={styles.balanceValue}>{recetasCruceKpis.worstWeekday?.diaSemana || 'Sin datos'}</strong>
                <span style={styles.balanceHint}>
                  {recetasCruceKpis.worstWeekday
                    ? `${formatPrecisePercent(recetasCruceKpis.worstWeekday.porcentajeMatch)} de match sobre ${formatNumber(recetasCruceKpis.worstWeekday.totalRegistros)} registros`
                    : 'Todavía no hay volumen suficiente en la ventana elegida'}
                </span>
              </div>
              <div style={styles.balanceCard}>
                <span style={styles.balanceLabel}>Pico de volumen</span>
                <strong style={styles.balanceValue}>{formatNumber(recetasCruceKpis.peakVolumeDay?.totalRegistros || 0)}</strong>
                <span style={styles.balanceHint}>
                  {recetasCruceKpis.peakVolumeDay
                    ? `${formatLongDate(recetasCruceKpis.peakVolumeDay.date)} con ${formatPrecisePercent(recetasCruceKpis.peakVolumeDay.porcentajeMatch)} de match`
                    : 'Sin actividad suficiente en el rango seleccionado'}
                </span>
              </div>
              <div style={styles.balanceCard}>
                <span style={styles.balanceLabel}>Brecha sin match</span>
                <strong style={styles.balanceValue}>{formatNumber(recetasCruceKpis.unmatchedRange)}</strong>
                <span style={styles.balanceHint}>{`${formatNumber(recetasCruceKpis.qualityGap)} casos se explican por solo PLEX, solo GECLISA o datos incompletos en el rango`}</span>
              </div>
            </div>

            <div style={{ ...styles.insightGrid, marginTop: 18 }} className="home-insight-grid">
              <InsightMetric
                eyebrow="Promedio del rango"
                title="Volumen diario"
                value={formatNumber(Math.round(recetasCruceKpis.averageDailyVolumeRange))}
                hint={`${formatNumber(recetasCruceKpis.activeDaysRange)} días con actividad dentro del período aplicado`}
                tone="navy"
              />
              <InsightMetric
                eyebrow="Promedio del rango"
                title="Matches por día"
                value={formatNumber(Math.round(recetasCruceKpis.averageDailyMatchRange))}
                hint="Promedio operativo de cruces efectivos recientes"
                tone="emerald"
              />
              <InsightMetric
                eyebrow="Consistencia"
                title="Días con match"
                value={formatPrecisePercent(recetasCruceKpis.consistencyPercent)}
                hint={`${formatNumber(recetasCruceKpis.daysWithAnyMatch)} jornadas con al menos un cruce efectivo`}
                tone="amber"
              />
              <InsightMetric
                eyebrow="Delta cierre"
                title="Último vs previo"
                value={formatSignedPoints(recetasCruceKpis.todayVsYesterdayPoints)}
                hint="Variación en puntos porcentuales del ratio de match diario"
                tone={recetasCruceKpis.todayVsYesterdayPoints >= 0 ? 'amber' : 'rose'}
              />
            </div>

            <div style={{ ...styles.dualGrid, marginTop: 18 }} className="home-dual-grid">
              <div
                style={styles.balanceCard}
                className={recetasCruceKpisLoading ? 'home-daily-detail-card home-daily-detail-card--loading' : 'home-daily-detail-card'}
              >
                <div className="home-daily-detail-card__head">
                  <div style={styles.panelEyebrow}>Detalle diario del rango</div>
                  {recetasCruceKpisLoading ? (
                    <span className="home-daily-detail-card__badge">Actualizando</span>
                  ) : null}
                </div>
                {recetasCruceKpisLoading ? (
                  <DailyRangeDetailSkeleton rowCount={appliedRangeDays} />
                ) : (
                  <>
                    <div style={{ marginTop: 8, fontSize: 12, color: CRM.textLight, lineHeight: 1.5 }}>
                      Vista completa del período aplicado, con scroll y lectura rápida por día.
                    </div>
                    <div
                      style={{ marginTop: 12, display: 'grid', gap: 10, maxHeight: 420, overflowY: 'auto', paddingRight: 4 }}
                      className="home-daily-detail-list"
                    >
                      {recetasCruceKpis.recentDays.length === 0 ? (
                        <div className="home-daily-detail-empty">
                          <strong>Sin días con actividad</strong>
                          <span>No hay registros en el rango aplicado. Probá ampliar las fechas.</span>
                        </div>
                      ) : null}
                      {recetasCruceKpis.recentDays.map((day) => (
                    <div
                      key={day.date}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '84px minmax(0, 1fr) auto',
                        gap: 10,
                        alignItems: 'center',
                        padding: '10px 12px',
                        borderRadius: 12,
                        background: '#F8FAFC',
                        border: '1px solid #E2E8F0'
                      }}
                    >
                      <div>
                        <strong style={{ display: 'block', color: CRM.navy, fontSize: 13 }}>{formatShortDate(day.date)}</strong>
                        <span style={{ fontSize: 11, color: CRM.textXlight }}>{day.date}</span>
                      </div>
                      <div>
                        <div style={{ color: CRM.textLight, fontSize: 12 }}>
                          {formatNumber(day.totalMatch)} / {formatNumber(day.totalRegistros)} match/registros
                        </div>
                        <div style={{ marginTop: 8, height: 8, borderRadius: 999, background: '#E2E8F0', overflow: 'hidden' }}>
                          <div
                            style={{
                              width: `${clampPercent(day.porcentajeMatch)}%`,
                              height: '100%',
                              borderRadius: 999,
                              background: 'linear-gradient(90deg, #10B981 0%, #2365AB 100%)'
                            }}
                          />
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <strong style={{ display: 'block', color: CRM.navy, fontSize: 13 }}>{formatPrecisePercent(day.porcentajeMatch)}</strong>
                        <span style={{ fontSize: 11, color: CRM.textXlight }}>{formatNumber(day.soloGeclisa + day.soloPlex + day.sinDatos)} brecha operativa</span>
                      </div>
                    </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div
                style={styles.balanceCard}
                className={recetasCruceKpisLoading ? 'home-daily-detail-card home-daily-detail-card--loading' : 'home-daily-detail-card'}
              >
                <div className="home-daily-detail-card__head">
                  <div style={styles.panelEyebrow}>Patrón por día de semana</div>
                  {recetasCruceKpisLoading ? (
                    <span className="home-daily-detail-card__badge">Actualizando</span>
                  ) : null}
                </div>
                {recetasCruceKpisLoading ? (
                  <WeekdayPatternSkeleton />
                ) : (
                  <>
                    <div style={{ marginTop: 8, fontSize: 12, color: CRM.textLight, lineHeight: 1.5 }}>
                      Resumen del rango y desglose por día de la semana (lun–dom).
                    </div>
                    <div
                      style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}
                      className="home-kpi-range-summary-row"
                    >
                      <div style={{ padding: '10px 12px', borderRadius: 12, background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: CRM.textXlight }}>Registros del rango</div>
                        <strong style={{ display: 'block', marginTop: 6, color: CRM.navy, fontSize: 22 }}>{formatNumber(recetasCruceKpis.totalRangeRegistros)}</strong>
                      </div>
                      <div style={{ padding: '10px 12px', borderRadius: 12, background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: CRM.textXlight }}>Matches del rango</div>
                        <strong style={{ display: 'block', marginTop: 6, color: CRM.navy, fontSize: 22 }}>{formatNumber(recetasCruceKpis.totalRangeMatch)}</strong>
                      </div>
                      <div style={{ padding: '10px 12px', borderRadius: 12, background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: CRM.textXlight }}>Solo PLEX</div>
                        <strong style={{ display: 'block', marginTop: 6, color: CRM.navy, fontSize: 22 }}>{formatNumber(recetasCruceKpis.totalRangeSoloPlex)}</strong>
                      </div>
                      <div style={{ padding: '10px 12px', borderRadius: 12, background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: CRM.textXlight }}>Días sin match</div>
                        <strong style={{ display: 'block', marginTop: 6, color: CRM.navy, fontSize: 22 }}>{formatNumber(recetasCruceKpis.zeroMatchDaysRange)}</strong>
                      </div>
                    </div>
                    <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                      {recetasCruceKpis.weekdayStats.length === 0 ? (
                        <div className="home-daily-detail-empty">
                          <strong>Sin datos por día de semana</strong>
                          <span>No hay suficiente actividad en el rango para armar el patrón semanal.</span>
                        </div>
                      ) : null}
                      {recetasCruceKpis.weekdayStats.map((day) => (
                        <div
                          key={day.dow}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '92px 1fr auto',
                            gap: 10,
                            alignItems: 'center',
                            padding: '10px 12px',
                            borderRadius: 12,
                            background: '#F8FAFC',
                            border: '1px solid #E2E8F0'
                          }}
                        >
                          <strong style={{ color: CRM.navy, fontSize: 13 }}>{day.diaSemana}</strong>
                          <span style={{ color: CRM.textLight, fontSize: 12 }}>
                            {formatNumber(day.totalMatch)} matches sobre {formatNumber(day.totalRegistros)} registros
                          </span>
                          <strong style={{ color: CRM.navy, fontSize: 13 }}>{formatPrecisePercent(day.porcentajeMatch)}</strong>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>

          <section style={{ ...styles.dualGrid, marginTop: 24 }} className="home-dual-grid">
            <div style={styles.panel} className="home-panel">
              <div style={styles.panelHeader} className="home-panel-header">
                <div>
                  <div style={styles.panelEyebrow}>Mapa de asociación</div>
                  <h3 style={styles.panelTitle}>Pacientes y cobertura de contacto</h3>
                </div>
                <div style={styles.panelTag}>PLEX ↔ GECLISA</div>
              </div>

              <div style={styles.progressGrid}>
                <ProgressInsight
                  title="Cruce maestro de pacientes"
                  helper={`${formatNumber(totalCruzados)} de ${formatNumber(total)} registros del universo total están consolidados`}
                  value={formatNumber(totalCruzados)}
                  percent={derived.tasaCrucePacientes}
                  tone={getTone(derived.tasaCrucePacientes)}
                />
                <ProgressInsight
                  title="Con teléfono en PLEX"
                  helper={`${formatNumber(conTelefonoPlex)} de ${formatNumber(totalCruzados)} cruzados tienen teléfono cargado en PLEX`}
                  value={formatNumber(conTelefonoPlex)}
                  percent={derived.tasaTelefonoPlex}
                  tone={getTone(derived.tasaTelefonoPlex)}
                />
                <ProgressInsight
                  title="Con teléfono en GECLISA"
                  helper={`${formatNumber(conTelefonoOCelGeclisa)} de ${formatNumber(totalCruzados)} cruzados tienen teléfono o celular en GECLISA`}
                  value={formatNumber(conTelefonoOCelGeclisa)}
                  percent={derived.tasaTelefonoGeclisa}
                  tone={getTone(derived.tasaTelefonoGeclisa)}
                />
                <ProgressInsight
                  title="Con algun canal de contacto"
                  helper={`${formatNumber(conAlgunContacto)} de ${formatNumber(totalCruzados)} cruzados tienen al menos un dato util en alguno de los dos sistemas`}
                  value={formatNumber(conAlgunContacto)}
                  percent={derived.tasaAlgunContacto}
                  tone={getTone(derived.tasaAlgunContacto)}
                />
              </div>
            </div>

            <div style={styles.panel} className="home-panel">
              <div style={styles.panelHeader} className="home-panel-header">
                <div>
                  <div style={styles.panelEyebrow}>Trazabilidad clinica</div>
                  <h3 style={styles.panelTitle}>Recetas y nivel de validación</h3>
                </div>
                <div style={styles.panelTag}>GECLISA → PLEX</div>
              </div>

              <div style={styles.progressGrid}>
                <ProgressInsight
                  title="Recetas con identificador"
                  helper="Base minima para seguir el ciclo completo"
                  value={formatNumber(recetasConIdentificador)}
                  percent={derived.tasaIdentificador}
                  tone={getTone(derived.tasaIdentificador)}
                />
                <ProgressInsight
                  title="Cruce confirmado con PLEX"
                  helper="Identificador GECLISA alineado con número de receta PLEX"
                  value={formatNumber(recetasCruceConPlex)}
                  percent={derived.tasaCruceRecetas}
                  tone={getTone(derived.tasaCruceRecetas)}
                />
                <ProgressInsight
                  title="Validadas sin match"
                  helper="Ya están identificadas, pero no cierran todavía el cruce"
                  value={formatNumber(recetasSinCruceValidada)}
                  percent={derived.tasaPendienteRecetas}
                  tone={derived.tasaPendienteRecetas >= 45 ? 'bad' : 'warn'}
                />
                <ProgressInsight
                  title="Recetas sin identificador"
                  helper="Bolsa principal de mejora para trazabilidad futura"
                  value={formatNumber(recetasSinIdentificador)}
                  percent={getPercent(recetasSinIdentificador, recetasEmitidasSanatorio)}
                  tone={recetasSinIdentificador > 0 ? 'bad' : 'good'}
                />
              </div>
            </div>
          </section>

          <section style={styles.trendChartSection} className="home-panel">
            {hasCurrentMonthInSeries ? (
              <div style={styles.trendToggleRow}>
                <button
                  type="button"
                  style={styles.trendToggleBtn}
                  onClick={() => setShowCurrentPartialMonth((prev) => !prev)}
                >
                  {showCurrentPartialMonth ? 'Ocultar mes en curso' : 'Mostrar mes en curso'}
                </button>
              </div>
            ) : null}
            <TrendLineChart
              labels={monthlyLabels}
              fullLabels={monthlyFullLabels}
              series={monthlyChartSeries}
              title={monthlyChartTitle}
              loading={monthlyLoading}
            />
          </section>

          <section style={styles.dualGrid} className="home-dual-grid">
            <div style={styles.panel} className="home-panel">
              <div style={styles.panelHeader} className="home-panel-header">
                <div>
                  <div style={styles.panelEyebrow}>Pulso operacional</div>
                  <h3 style={styles.panelTitle}>Dónde se mueve hoy la operación</h3>
                </div>
                <div style={styles.panelTag}>
                  {derived.farmaciaDominante ? 'Predomina Farmacia' : 'Predomina Sanatorio'}
                </div>
              </div>

              <div style={styles.splitBar}>
                <div
                  style={{
                    ...styles.splitBarSegmentLeft,
                    width: `${getPercent(ultFarmacia, ultFarmacia + ultSanatorio)}%`
                  }}
                />
                <div
                  style={{
                    ...styles.splitBarSegmentRight,
                    width: `${getPercent(ultSanatorio, ultFarmacia + ultSanatorio)}%`
                  }}
                />
              </div>

              <div style={styles.balanceGrid} className="home-balance-grid">
                <div style={styles.balanceCard}>
                  <span style={styles.balanceLabel}>Farmacia GSA</span>
                  <strong style={styles.balanceValue}>{formatNumber(ultFarmacia)}</strong>
                  <span style={styles.balanceHint}>Último movimiento registrado en farmacia</span>
                </div>
                <div style={styles.balanceCard}>
                  <span style={styles.balanceLabel}>Sanatorio Regional</span>
                  <strong style={styles.balanceValue}>{formatNumber(ultSanatorio)}</strong>
                  <span style={styles.balanceHint}>Último movimiento registrado en sanatorio</span>
                </div>
              </div>
            </div>

            <div style={styles.panel} className="home-panel">
              <div style={styles.panelHeader} className="home-panel-header">
                <div>
                  <div style={styles.panelEyebrow}>Acción sugerida</div>
                  <h3 style={styles.panelTitle}>Entradas rapidas de trabajo</h3>
                </div>
                <div style={styles.panelTag}>Navegación</div>
              </div>

              <div style={styles.quickLinksGrid} className="home-quick-links-grid">
                <QuickLinkCard
                  to="/asociacion-clientes"
                  icon="👥"
                  title="Explorar asociación de clientes"
                  description="Entrar al universo de pacientes, cruce maestro y cobertura de contacto."
                  accentLabel="Clientes"
                />
                <QuickLinkCard
                  to="/recetas-cruzadas"
                  icon="💊"
                  title="Auditar recetas cruzadas"
                  description="Revisar trazabilidad, pendientes y matches efectivos entre GECLISA y PLEX."
                  accentLabel="Recetas"
                />
              </div>
            </div>
          </section>
        </>
      )}

      <style>
        {`
          .home-stat-pill {
            display: inline-flex;
            flex-direction: column;
            gap: 4px;
            padding: 10px 12px;
            border-radius: 14px;
            min-width: 132px;
            background: rgba(255,255,255,0.12);
            border: 1px solid rgba(255,255,255,0.12);
            backdrop-filter: blur(12px);
          }
          .home-stat-pill__label {
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: rgba(255,255,255,0.7);
          }
          .home-stat-pill__value {
            font-size: 22px;
            line-height: 1;
            color: #fff;
          }
          .home-stat-pill--good {
            box-shadow: inset 0 0 0 1px rgba(110,231,183,0.24);
          }
          .home-stat-pill--warn {
            box-shadow: inset 0 0 0 1px rgba(251,191,36,0.24);
          }
          .home-stat-pill--bad {
            box-shadow: inset 0 0 0 1px rgba(251,113,133,0.24);
          }
          .home-insight-card {
            position: relative;
            padding: 1.15rem 1.1rem;
            border-radius: 18px;
            background: #fff;
            border: 1px solid ${CRM.border};
            box-shadow: 0 10px 24px rgba(15,23,42,0.06);
            overflow: hidden;
          }
          .home-insight-card::before {
            content: '';
            position: absolute;
            inset: 0 auto 0 0;
            width: 4px;
            border-radius: 18px 0 0 18px;
            background: ${CRM.orange};
          }
          .home-insight-card--navy::before {
            background: ${CRM.navyMid};
          }
          .home-insight-card--amber::before {
            background: #f59e0b;
          }
          .home-insight-card--emerald::before {
            background: #10b981;
          }
          .home-insight-card--rose::before {
            background: #f43f5e;
          }
          .home-insight-card__eyebrow {
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: ${CRM.textXlight};
          }
          .home-insight-card__value {
            display: block;
            margin-top: 10px;
            font-size: 30px;
            line-height: 1;
            color: ${CRM.navy};
          }
          .home-insight-card__title {
            margin-top: 8px;
            font-size: 14px;
            font-weight: 700;
            color: ${CRM.text};
          }
          .home-insight-card__hint {
            margin-top: 4px;
            font-size: 12px;
            color: ${CRM.textXlight};
            line-height: 1.5;
          }
          .home-quick-link {
            will-change: transform, box-shadow, border-color;
          }
          .home-quick-link:hover {
            transform: translateY(-4px);
            box-shadow: 0 16px 28px rgba(15,23,42,0.12);
            border-color: rgba(35,101,171,0.35);
          }
          .home-daily-detail-card__head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            flex-wrap: wrap;
          }
          .home-daily-detail-card__badge {
            display: inline-flex;
            align-items: center;
            padding: 4px 10px;
            border-radius: 999px;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            color: #1C4F86;
            background: #E8F2FC;
            border: 1px solid #B8D4EB;
          }
          .home-daily-detail-card--loading {
            background: #FAFCFE;
            border-color: #C5D9EB;
          }
          .home-daily-detail-loading {
            margin-top: 14px;
            padding: 14px;
            border-radius: 14px;
            background: #F8FBFF;
            border: 1px solid #D8E6F2;
          }
          .home-daily-detail-loading__banner {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding-bottom: 14px;
            margin-bottom: 12px;
            border-bottom: 1px solid #E2EAF2;
          }
          .home-daily-detail-loading__spinner {
            flex-shrink: 0;
            width: 22px;
            height: 22px;
            margin-top: 2px;
            border-radius: 50%;
            border: 2px solid #D8E6F2;
            border-top-color: #2365AB;
            animation: home-daily-spin 0.75s linear infinite;
          }
          .home-daily-detail-loading__copy {
            display: grid;
            gap: 4px;
            min-width: 0;
          }
          .home-daily-detail-loading__copy strong {
            font-size: 14px;
            color: ${CRM.navy};
          }
          .home-daily-detail-loading__copy span {
            font-size: 12px;
            line-height: 1.45;
            color: ${CRM.textLight};
          }
          .home-daily-detail-loading__list {
            display: grid;
            gap: 10px;
            max-height: 360px;
            overflow: hidden;
          }
          .home-daily-detail-loading__row {
            display: grid;
            grid-template-columns: 84px minmax(0, 1fr) auto;
            gap: 10px;
            align-items: center;
            padding: 10px 12px;
            border-radius: 12px;
            background: #FFFFFF;
            border: 1px solid #E8EEF4;
          }
          .home-daily-detail-loading__date,
          .home-daily-detail-loading__main,
          .home-daily-detail-loading__side {
            display: grid;
            gap: 8px;
          }
          .home-daily-detail-loading__side {
            justify-items: end;
          }
          .home-daily-detail-skel {
            display: block;
            border-radius: 6px;
            background-color: #E8EEF4;
            background-image: linear-gradient(
              90deg,
              #E8EEF4 0%,
              #F4F7FA 45%,
              #E8EEF4 90%
            );
            background-size: 200% 100%;
            animation: home-daily-shimmer 1.35s ease-in-out infinite;
          }
          .home-daily-detail-skel--title {
            height: 12px;
            width: 58px;
          }
          .home-daily-detail-skel--sub {
            height: 10px;
            width: 72px;
          }
          .home-daily-detail-skel--line {
            height: 10px;
            width: 100%;
          }
          .home-daily-detail-skel--bar {
            height: 8px;
            width: 100%;
            border-radius: 999px;
          }
          .home-daily-detail-skel--pct {
            height: 12px;
            width: 48px;
          }
          .home-daily-detail-list {
            scrollbar-gutter: stable;
          }
          .home-daily-detail-empty {
            display: grid;
            gap: 6px;
            padding: 18px 16px;
            border-radius: 12px;
            text-align: center;
            background: #F8FAFC;
            border: 1px dashed #CBD5E1;
          }
          .home-daily-detail-empty strong {
            font-size: 14px;
            color: ${CRM.navy};
          }
          .home-daily-detail-empty span {
            font-size: 12px;
            color: ${CRM.textLight};
            line-height: 1.45;
          }
          @keyframes home-daily-spin {
            to {
              transform: rotate(360deg);
            }
          }
          @keyframes home-daily-shimmer {
            0% {
              background-position: 100% 0;
            }
            100% {
              background-position: -100% 0;
            }
          }
          .home-weekday-pattern-loading {
            margin-top: 14px;
            padding: 14px;
            border-radius: 14px;
            background: #F8FBFF;
            border: 1px solid #D8E6F2;
          }
          .home-weekday-pattern-loading__metrics {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 10px;
            margin-top: 14px;
          }
          .home-weekday-pattern-loading__metric {
            display: grid;
            gap: 10px;
            padding: 10px 12px;
            border-radius: 12px;
            background: #FFFFFF;
            border: 1px solid #E8EEF4;
          }
          .home-daily-detail-skel--metric-label {
            height: 10px;
            width: 78%;
            max-width: 120px;
          }
          .home-daily-detail-skel--metric-value {
            height: 22px;
            width: 56%;
            max-width: 88px;
          }
          .home-weekday-pattern-loading__list {
            display: grid;
            gap: 10px;
            margin-top: 12px;
          }
          .home-weekday-pattern-loading__row {
            display: grid;
            grid-template-columns: 92px 1fr auto;
            gap: 10px;
            align-items: center;
            padding: 10px 12px;
            border-radius: 12px;
            background: #FFFFFF;
            border: 1px solid #E8EEF4;
          }
          .home-daily-detail-skel--weekday {
            height: 12px;
            width: 72px;
          }
          @media (prefers-reduced-motion: reduce) {
            .home-daily-detail-loading__spinner,
            .home-daily-detail-skel {
              animation: none;
            }
          }
          .home-hero-main,
          .home-hero-score-card,
          .home-panel,
          .home-quick-link,
          .home-stat-pill,
          .home-header-badge {
            box-sizing: border-box;
            max-width: 100%;
          }
          @keyframes home-kpi-sync-spin {
            to {
              transform: rotate(360deg);
            }
          }
          @media (max-width: 992px) {
            .home-header {
              flex-direction: column !important;
              align-items: stretch !important;
            }
            .home-header-badge {
              width: 100% !important;
              justify-content: center !important;
            }
            .home-hero-panel,
            .home-insight-grid,
            .home-dual-grid,
            .home-balance-grid,
            .home-quick-links-grid {
              grid-template-columns: 1fr !important;
            }
            .home-role-hero-panel {
              grid-template-columns: 1fr !important;
            }
            .home-hero-main,
            .home-hero-score-card,
            .home-panel,
            .home-quick-link {
              width: 100% !important;
              max-width: 100% !important;
            }
            .home-hero-pills {
              flex-direction: column !important;
            }
            .home-role-pills {
              display: grid !important;
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
              gap: 10px !important;
            }
            .home-role-pills > div {
              min-width: 0 !important;
              width: 100% !important;
            }
            .home-stat-pill {
              min-width: 100% !important;
              width: 100% !important;
            }
            .home-panel-header {
              flex-direction: column !important;
              align-items: stretch !important;
            }
            .home-panel-header > div:last-child {
              align-self: flex-start !important;
            }
          }
          @media (max-width: 768px) {
            .home-kpi-range-summary-row {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }
          @media (max-width: 480px) {
            .home-kpi-range-summary-row {
              grid-template-columns: minmax(0, 1fr) !important;
            }
          }
        `}
      </style>
    </div>
  );
}

const styles = {
  container: {
    padding: '1.75rem 1.5rem',
    margin: '0 auto',
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box'
  },
  header: {
    marginBottom: '1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    alignItems: 'flex-start',
    flexWrap: 'wrap'
  },
  title: {
    fontSize: '1.8rem',
    marginBottom: '0.5rem',
    color: CRM.navy
  },
  subtitle: {
    color: CRM.textLight,
    fontSize: '1rem'
  },
  headerBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.55rem 0.85rem',
    borderRadius: 999,
    border: `1px solid ${CRM.border}`,
    background: CRM.white,
    color: CRM.textLight,
    fontSize: 12,
    fontWeight: 600
  },
  kpis: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    marginBottom: '1.5rem',
    width: '100%'
  },
  heroPanel: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.6fr) minmax(260px, 0.8fr)',
    gap: '1rem',
    marginBottom: '1rem'
  },
  heroMain: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 24,
    padding: '1.6rem',
    background: `linear-gradient(135deg, ${CRM.navy} 0%, ${CRM.navyMid} 48%, ${CRM.orangeDark} 100%)`,
    color: CRM.white,
    boxShadow: '0 18px 40px rgba(37,51,66,0.18)'
  },
  heroEyebrow: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    color: 'rgba(255,255,255,0.68)',
    fontWeight: 700
  },
  heroTitle: {
    margin: '0.55rem 0 0',
    fontSize: '2rem',
    lineHeight: 1.08
  },
  heroDescription: {
    margin: '0.7rem 0 0',
    maxWidth: 680,
    color: 'rgba(255,255,255,0.82)',
    lineHeight: 1.65,
    fontSize: 14
  },
  heroPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    marginTop: '1.25rem'
  },
  heroScoreCard: {
    borderRadius: 24,
    border: `1px solid ${CRM.border}`,
    background: CRM.white,
    padding: '1.35rem 1.2rem',
    boxShadow: '0 18px 40px rgba(15,23,42,0.06)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  heroProfileCard: {
    borderRadius: 24,
    border: `1px solid ${CRM.border}`,
    background: CRM.white,
    padding: '1.1rem 1.1rem',
    boxShadow: '0 18px 40px rgba(15,23,42,0.06)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '0.65rem'
  },
  heroProfileTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.95rem'
  },
  heroProfileAvatar: {
    width: 64,
    height: 64,
    borderRadius: 18,
    objectFit: 'cover',
    border: `2px solid ${CRM.blueLight}`,
    boxShadow: '0 10px 24px rgba(35,101,171,0.18)'
  },
  heroProfileIdentity: {
    minWidth: 0
  },
  heroProfileEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: CRM.textXlight
  },
  heroProfileName: {
    marginTop: 4,
    fontSize: 24,
    lineHeight: 1.05,
    fontWeight: 800,
    color: CRM.navy
  },
  heroProfileDescription: {
    fontSize: 13,
    lineHeight: 1.55,
    color: CRM.textLight
  },
  heroProfileDescriptionLead: {
    color: CRM.text,
    fontWeight: 700
  },
  heroProfileMeta: {
    padding: '0.7rem 0.8rem',
    borderRadius: 16,
    background: 'linear-gradient(135deg, #F8FBFF 0%, #EEF4FB 100%)',
    border: '1px solid #D9E5F2'
  },
  heroProfileMetaLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: CRM.textXlight
  },
  heroProfileMetaValue: {
    display: 'block',
    marginTop: 4,
    fontSize: 12,
    lineHeight: 1.45,
    color: CRM.navy
  },
  heroScoreLabel: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: CRM.textXlight
  },
  heroScoreValue: {
    marginTop: 8,
    fontSize: 56,
    lineHeight: 1,
    fontWeight: 800,
    color: CRM.navy
  },
  heroScoreHint: {
    marginTop: 10,
    fontSize: 13,
    lineHeight: 1.6,
    color: CRM.textLight
  },
  heroScoreTrack: {
    marginTop: 18,
    height: 10,
    borderRadius: 999,
    background: '#E2E8F0',
    overflow: 'hidden'
  },
  heroScoreBar: {
    height: '100%',
    borderRadius: 999,
    background: `linear-gradient(90deg, ${CRM.orange} 0%, ${CRM.orangeDark} 100%)`,
    transition: 'width 0.25s ease'
  },
  insightGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem'
  },
  dualGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem'
  },
  panel: {
    borderRadius: 22,
    border: `1px solid ${CRM.border}`,
    background: CRM.white,
    padding: '1.2rem',
    boxShadow: '0 16px 36px rgba(15,23,42,0.05)'
  },
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  panelEyebrow: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontWeight: 700,
    color: CRM.textXlight
  },
  panelTitle: {
    margin: '0.25rem 0 0',
    fontSize: '1.15rem',
    fontWeight: 700,
    color: CRM.navy
  },
  panelTag: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 'fit-content',
    padding: '0.45rem 0.7rem',
    borderRadius: 999,
    background: CRM.blueLight,
    color: CRM.textXlight,
    fontWeight: 600,
    fontSize: 12
  },
  progressGrid: {
    display: 'grid',
    gap: '0.9rem',
    marginTop: '1rem'
  },
  progressCard: {
    borderRadius: 16,
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    padding: '0.95rem 1rem'
  },
  progressHead: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    alignItems: 'flex-start'
  },
  progressTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: CRM.text
  },
  progressHelper: {
    marginTop: 4,
    fontSize: 12,
    lineHeight: 1.55,
    color: CRM.textXlight
  },
  progressValueWrap: {
    textAlign: 'right',
    minWidth: 80
  },
  progressValue: {
    display: 'block',
    fontSize: 22,
    lineHeight: 1,
    color: CRM.navy
  },
  progressPercent: {
    display: 'block',
    marginTop: 4,
    fontSize: 12,
    fontWeight: 700,
    color: CRM.textXlight
  },
  progressTrack: {
    marginTop: 12,
    height: 10,
    borderRadius: 999,
    background: '#E2E8F0',
    overflow: 'hidden'
  },
  trendChartCard: {
    borderRadius: 22,
    border: `1px solid ${CRM.border}`,
    background: CRM.white,
    padding: '1.3rem',
    boxShadow: '0 18px 40px rgba(15,23,42,0.06)',
    marginBottom: '1rem'
  },
  trendChartSection: {
    borderRadius: 22,
    border: `1px solid ${CRM.border}`,
    background: CRM.white,
    padding: '1.2rem',
    boxShadow: '0 16px 36px rgba(15,23,42,0.05)',
    marginBottom: '1.6rem'
  },
  trendToggleRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '0.75rem'
  },
  trendToggleBtn: {
    border: `1px solid ${CRM.border}`,
    background: '#fff',
    color: CRM.navyMid,
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer'
  },
  trendChartLoadingWrap: {
    minHeight: 320,
    borderRadius: 18,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.9rem',
    background: 'radial-gradient(circle at 20% 15%, rgba(35,101,171,0.08), transparent 55%), radial-gradient(circle at 80% 85%, rgba(28,79,134,0.1), transparent 48%)'
  },
  trendChartSpinner: {
    width: 54,
    height: 54,
    borderRadius: 999,
    border: '4px solid #E2E8F0',
    borderTopColor: CRM.orange,
    borderRightColor: CRM.orangeDark,
    animation: 'home-kpi-sync-spin 1s linear infinite',
    boxShadow: '0 0 22px rgba(35,101,171,0.28)'
  },
  trendChartLoadingText: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '0.03em',
    color: CRM.textLight,
    textTransform: 'uppercase'
  },
  trendChartHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    flexWrap: 'wrap',
    marginBottom: '1.25rem'
  },
  trendChartEyebrow: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: CRM.textXlight,
    fontWeight: 700
  },
  trendChartTitle: {
    marginTop: 4,
    fontSize: '1.1rem',
    fontWeight: 700,
    color: CRM.navy
  },
  trendChartHint: {
    marginTop: 6,
    fontSize: 13,
    color: CRM.textLight,
    lineHeight: 1.7,
    maxWidth: 520
  },
  trendChartLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.9rem',
    alignItems: 'center'
  },
  trendChartLegendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    color: CRM.textLight,
    fontWeight: 600
  },
  trendChartLegendDot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    display: 'inline-block'
  },
  trendValuesRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0.85rem',
    marginTop: '1rem'
  },
  trendValuesItem: {
    borderRadius: 14,
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    padding: '0.9rem 1rem'
  },
  trendValuesLabel: {
    fontSize: 12,
    color: CRM.textXlight,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em'
  },
  trendValuesValue: {
    display: 'block',
    marginTop: 8,
    fontSize: 24,
    fontWeight: 800,
    lineHeight: 1
  },
  splitBar: {
    display: 'flex',
    height: 18,
    borderRadius: 999,
    overflow: 'hidden',
    background: '#E2E8F0',
    marginTop: '1rem'
  },
  progressBar: {
    height: '100%',
    borderRadius: 999
  },
  splitBarSegmentLeft: {
    background: `linear-gradient(90deg, ${CRM.orange} 0%, ${CRM.orangeDark} 100%)`
  },
  splitBarSegmentRight: {
    background: `linear-gradient(90deg, ${CRM.navyMid} 0%, ${CRM.slate} 100%)`
  },
  balanceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0.9rem',
    marginTop: '1rem'
  },
  balanceCard: {
    borderRadius: 16,
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    padding: '1rem'
  },
  balanceLabel: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: CRM.textXlight
  },
  balanceValue: {
    display: 'block',
    marginTop: 8,
    fontSize: 30,
    lineHeight: 1,
    color: CRM.navy
  },
  balanceHint: {
    display: 'block',
    marginTop: 8,
    fontSize: 12,
    lineHeight: 1.5,
    color: CRM.textLight
  },
  quickLinksGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '0.9rem',
    marginTop: '1rem'
  },
  quickLinkCard: {
    padding: '1rem',
    borderRadius: 18,
    background: '#fff',
    textDecoration: 'none',
    color: CRM.navy,
    border: `1px solid ${CRM.border}`,
    transition: 'all 0.2s ease',
    boxShadow: '0 10px 24px rgba(15,23,42,0.06)',
    display: 'flex',
    flexDirection: 'column'
  },
  quickLinkTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem'
  },
  quickLinkIcon: {
    fontSize: 22
  },
  quickLinkAccent: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.35rem 0.65rem',
    borderRadius: 999,
    background: CRM.orangeLight,
    color: CRM.orangeDark,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em'
  },
  quickLinkTitle: {
    marginTop: 14,
    fontSize: 16,
    fontWeight: 700
  },
  quickLinkDescription: {
    marginTop: 6,
    fontSize: 13,
    lineHeight: 1.6,
    color: CRM.textLight
  },
  quickLinkAction: {
    marginTop: 14,
    fontSize: 12,
    fontWeight: 700,
    color: CRM.orangeDark
  }
};

const progressToneStyles = {
  good: {
    background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
  },
  warn: {
    background: 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)'
  },
  bad: {
    background: 'linear-gradient(90deg, #f43f5e 0%, #fb7185 100%)'
  }
};

export default Home;
